<script setup lang="ts">
import { ref, computed, watch } from 'vue'

const props = defineProps<{
  currentPhotoUrl?: string | null
  uploadEndpoint: 'inventory' | 'consumables'
}>()

const emit = defineEmits<{
  (e: 'uploaded', key: string): void
  (e: 'removed'): void
  (e: 'error', message: string): void
}>()

const fileInput = ref<HTMLInputElement | null>(null)
const previewUrl = ref<string | null>(null)
const uploading = ref(false)
const dragOver = ref(false)

const displayUrl = computed(() => previewUrl.value || props.currentPhotoUrl)

const API_BASE = import.meta.env.VITE_API_URL || ''

function getToken(): string | null {
  return localStorage.getItem('session_token')
}

function selectFile() {
  fileInput.value?.click()
}

async function handleFileChange(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (file) {
    await uploadFile(file)
  }
  // Reset input so same file can be selected again
  input.value = ''
}

async function handleDrop(event: DragEvent) {
  dragOver.value = false
  const file = event.dataTransfer?.files?.[0]
  if (file && file.type.startsWith('image/')) {
    await uploadFile(file)
  }
}

function handleDragOver(event: DragEvent) {
  event.preventDefault()
  dragOver.value = true
}

function handleDragLeave() {
  dragOver.value = false
}

async function uploadFile(file: File) {
  // Validate file type
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
  if (!allowedTypes.includes(file.type)) {
    emit('error', 'Invalid file type. Please use JPEG, PNG, WebP, or GIF.')
    return
  }

  // Validate file size (10MB max)
  if (file.size > 10 * 1024 * 1024) {
    emit('error', 'File too large. Maximum 10MB.')
    return
  }

  // Show preview immediately
  previewUrl.value = URL.createObjectURL(file)
  uploading.value = true

  try {
    const token = getToken()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (token) {
      headers['X-Session-Token'] = token
    }

    // Get upload URL
    const urlResponse = await fetch(`${API_BASE}/api/${props.uploadEndpoint}/upload-url`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ filename: file.name, contentType: file.type }),
    })

    if (!urlResponse.ok) {
      throw new Error('Failed to get upload URL')
    }

    const { uploadUrl, key } = await urlResponse.json()

    // Upload to R2
    const uploadResponse = await fetch(`${API_BASE}${uploadUrl}`, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
        ...(token ? { 'X-Session-Token': token } : {}),
      },
      body: file,
    })

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload file')
    }

    emit('uploaded', key)
  } catch (err) {
    console.error('Upload error:', err)
    // Revert preview on error
    previewUrl.value = null
    emit('error', err instanceof Error ? err.message : 'Upload failed')
  } finally {
    uploading.value = false
  }
}

function removePhoto() {
  previewUrl.value = null
  emit('removed')
}

// Clean up object URL when component unmounts or preview changes
watch(previewUrl, (newUrl, oldUrl) => {
  if (oldUrl && oldUrl.startsWith('blob:')) {
    URL.revokeObjectURL(oldUrl)
  }
})
</script>

<template>
  <div class="photo-uploader">
    <!-- Current/Preview Photo -->
    <div
      v-if="displayUrl"
      class="relative rounded-lg overflow-hidden bg-gray-100"
    >
      <img
        :src="displayUrl.startsWith('/api') ? `${API_BASE}${displayUrl}` : displayUrl"
        alt="Photo"
        class="w-full h-48 object-cover"
      />
      <div
        v-if="uploading"
        class="absolute inset-0 bg-black/50 flex items-center justify-center"
      >
        <div class="text-white">Uploading...</div>
      </div>
      <button
        v-else
        @click="removePhoto"
        class="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
        title="Remove photo"
      >
        &times;
      </button>
    </div>

    <!-- Upload Area -->
    <div
      v-else
      @click="selectFile"
      @drop.prevent="handleDrop"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      class="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors"
      :class="dragOver ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-primary-400'"
    >
      <div v-if="uploading" class="text-gray-500">
        Uploading...
      </div>
      <div v-else class="text-gray-500">
        <p class="text-lg mb-2">Drop image here or click to select</p>
        <p class="text-sm">JPEG, PNG, WebP, or GIF up to 10MB</p>
      </div>
    </div>

    <!-- Hidden file input -->
    <input
      ref="fileInput"
      type="file"
      accept="image/jpeg,image/png,image/webp,image/gif"
      class="hidden"
      @change="handleFileChange"
    />
  </div>
</template>
